import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCurrentUser } from 'stores/useUserStore';
import { css } from 'styled-components';

import type { Props as BaseDialogProps } from 'components/ui/dialogs/Dialog';

import MultiLineText from 'components/core/typography/MultiLineText';
import Dialog from 'components/ui/dialogs/Dialog';
import { DialogIconContainer } from 'components/ui/dialogs/PromptDialog';
import { OptionsContainer } from 'components/ui/dialogs/PromptDialogOptions';
import { CheckIcon, ErrorIcon } from 'components/ui/icons';
import { DialogBodyContentContainer, DialogContentContainer, DialogHeader } from 'components/ui/layouts/DialogLayout';
import { ListItems } from 'components/ui/lists/CommonList';
import { Spinner } from 'components/ui/loading/Loader';
import Button from 'components/ui/shared/Buttons/Button';
import { MultilingualStringKey, setLocale, translate } from 'utils/intlUtils';

import { getApiErrorsMessage, logApiError } from 'store/api/graph/interfaces/apiErrors';
import { userModify } from 'store/api/graph/mutations/userModify';
import { client } from 'store/apollo/ApolloClient';

import { BLUE_500, RED_500, SPACE_0 } from 'styles/tokens';

const { t } = translate;

const LanguageToggle = (props: BaseDialogProps) => {
  const { id, metadata } = useCurrentUser();

  const { onCancel } = props;

  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState(null);
  const setProcessingRef = useRef(setIsProcessing);

  useEffect(() => {
    setProcessingRef.current = setIsProcessing;
  }, [setIsProcessing]);

  const onLanguageChange = useCallback(
    locale => async () => {
      setProcessingRef.current(true);

      try {
        await client.mutate({
          mutation: userModify,
          variables: {
            id,
            locale,
          },
        });

        setProcessingRef.current(false);
        setIsSuccess(true);
        setLocale(locale);

        setTimeout(() => globalThis.location.reload(), 700);
      } catch (error: any) {
        setProcessingRef.current(false);
        logApiError(error);
        setErrors(error);
      }
    },
    [setIsSuccess, id]
  );

  const onClose = useCallback(() => {
    onCancel?.();
    setErrors(null);
    setIsProcessing(false);
  }, [onCancel, setErrors, setIsProcessing]);

  // [#2893] Re-enable Spanish support when needed.
  const LanguageListItems = useMemo(
    () =>
      metadata?.locale
        ?.filter(locale => !locale?.id.startsWith(MultilingualStringKey.ES))
        .map(item => ({ id: item.id, label: item.name, onClick: onLanguageChange(item.id) })),
    [metadata, onLanguageChange]
  );

  //  TODO: Extract and standardize these full page error/processings/success states
  return (
    <Dialog {...props} isDismissible={!isProcessing && isSuccess}>
      {errors ? (
        <>
          <OptionsContainer>
            <ErrorIcon
              color={RED_500}
              css={css`
                padding-bottom: 10px;
              `}
              height={72}
              width={72}
            />
            <MultiLineText>{getApiErrorsMessage(errors)}</MultiLineText>
            <div>
              <Button fullWidth onClick={onClose} size="large">
                {t('okay')}
              </Button>
            </div>
          </OptionsContainer>
        </>
      ) : isProcessing ? (
        <DialogIconContainer>
          <Spinner />
        </DialogIconContainer>
      ) : isSuccess ? (
        <DialogIconContainer
          css={css`
            padding: ${SPACE_0};
          `}
        >
          <CheckIcon color={BLUE_500} height={48} width={48} />
        </DialogIconContainer>
      ) : (
        <DialogContentContainer
          css={css`
            min-width: 306px;
          `}
        >
          <DialogHeader onClose={onClose} showDivider title={t('language_one')} />
          <DialogBodyContentContainer>
            <ListItems items={LanguageListItems} />
          </DialogBodyContentContainer>
        </DialogContentContainer>
      )}
    </Dialog>
  );
};

export default LanguageToggle;
